import { useDeferredValue, useId, useMemo, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import type { McpToolDefinition } from "@lumen/shared";
import { t } from "../../i18n";
import { filterMcpActions, mcpActionGroup } from "./McpActionPicker.utils";

type Props = {
  tools: McpToolDefinition[];
  selectedNames: string[];
  onSelectionChange: (names: string[], checked: boolean) => void;
};

export function McpActionPicker({
  tools,
  selectedNames,
  onSelectionChange,
}: Props) {
  const [query, setQuery] = useState("");
  const [group, setGroup] = useState("");
  const [selectedOnly, setSelectedOnly] = useState(false);
  const search = useRef<HTMLInputElement>(null);
  const id = useId();
  const deferredQuery = useDeferredValue(query);
  const selected = useMemo(() => new Set(selectedNames), [selectedNames]);
  const largeCatalog = tools.length > 10;
  const groups = useMemo(
    () =>
      [
        ...new Set(
          tools.map((tool) => mcpActionGroup(tool.name)).filter(Boolean),
        ),
      ].sort(),
    [tools],
  );
  const visible = useMemo(
    () =>
      filterMcpActions(
        tools,
        deferredQuery,
        group,
        selectedOnly,
        selected,
      ).sort((a, b) => a.name.localeCompare(b.name)),
    [tools, deferredQuery, group, selectedOnly, selected],
  );
  const selectedCount = tools.filter((tool) => selected.has(tool.name)).length;
  const visibleSelected = visible.filter((tool) =>
    selected.has(tool.name),
  ).length;
  const filtered = Boolean(query.trim() || group || selectedOnly);
  const resetFilters = () => {
    setQuery("");
    setGroup("");
    setSelectedOnly(false);
    search.current?.focus();
  };
  const controlClass =
    "button min-h-11 border border-stone-200 px-3 text-xs text-stone-700 dark:border-white/15 dark:text-stone-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lumen-600";

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="shrink-0 space-y-3 border-b border-stone-200 px-2 py-3 dark:border-white/10">
        <div className="flex items-center justify-between gap-2 text-xs text-stone-600 dark:text-stone-300">
          <span>
            {t("{{selected}} of {{total}} actions selected", {
              selected: selectedCount,
              total: tools.length,
            })}
          </span>
          <button
            type="button"
            className={`${controlClass} aria-pressed:bg-lumen-50 aria-pressed:text-lumen-800 dark:aria-pressed:bg-lumen-950 dark:aria-pressed:text-lumen-200`}
            onClick={() => setSelectedOnly(!selectedOnly)}
            aria-pressed={selectedOnly}
          >
            {t("Selected only")}
          </button>
        </div>
        <div>
          <label
            htmlFor={`${id}-search`}
            className="mb-1.5 block text-xs font-medium"
          >
            {t("Search actions")}
          </label>
          <div className="relative">
            <Search
              aria-hidden="true"
              size={16}
              className="pointer-events-none absolute top-3.5 left-3 text-stone-500"
            />
            <input
              ref={search}
              id={`${id}-search`}
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t("Search names and descriptions…")}
              className="field w-full min-w-0 py-2.5 pr-11 pl-9 text-base sm:text-sm [&::-webkit-search-cancel-button]:hidden"
            />
            {query && (
              <button
                type="button"
                aria-label={t("Clear action search")}
                className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-lg focus-visible:outline-2 focus-visible:outline-lumen-600"
                onClick={() => {
                  setQuery("");
                  search.current?.focus();
                }}
              >
                <X aria-hidden="true" size={16} />
              </button>
            )}
          </div>
        </div>
        {largeCatalog && groups.length > 1 && (
          <div className="flex items-center gap-3">
            <label
              htmlFor={`${id}-group`}
              className="shrink-0 text-xs font-medium"
            >
              {t("Action group")}
            </label>
            <select
              id={`${id}-group`}
              value={group}
              onChange={(event) => setGroup(event.target.value)}
              className="field min-h-11 min-w-0 flex-1 text-sm"
            >
              <option value="">{t("All groups")}</option>
              {groups.map((name) => (
                <option key={name} value={name}>
                  {name} (
                  {
                    tools.filter((tool) => mcpActionGroup(tool.name) === name)
                      .length
                  }
                  )
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className={controlClass}
            disabled={!visible.length || visibleSelected === visible.length}
            onClick={() =>
              onSelectionChange(
                visible.map((tool) => tool.name),
                true,
              )
            }
          >
            {filtered ? t("Select results") : t("Select all")}
          </button>
          <button
            type="button"
            className={controlClass}
            disabled={!visibleSelected}
            onClick={() =>
              onSelectionChange(
                visible.map((tool) => tool.name),
                false,
              )
            }
          >
            {filtered ? t("Clear results") : t("Clear all")}
          </button>
          {filtered && (
            <button
              type="button"
              className={`${controlClass} ml-auto`}
              onClick={resetFilters}
            >
              {t("Reset filters")}
            </button>
          )}
        </div>
        <p
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="text-xs text-stone-600 dark:text-stone-300"
        >
          {t(
            "Showing {{visible}} of {{total}} actions · {{selected}} selected",
            {
              visible: visible.length,
              total: tools.length,
              selected: selectedCount,
            },
          )}
        </p>
      </div>
      <div
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-1 py-2"
        aria-label={t("MCP actions")}
        aria-busy={query !== deferredQuery}
      >
        {visible.length === 0 ? (
          <div className="px-3 py-8 text-center">
            <p className="text-sm font-medium">
              {selectedOnly && !selectedCount
                ? t("No actions selected")
                : t("No matching actions")}
            </p>
            <p className="mt-2 text-xs text-stone-600 dark:text-stone-300">
              {t(
                "Try another search or reset your filters to see all actions.",
              )}
            </p>
            <button
              type="button"
              className={`${controlClass} mt-4`}
              onClick={resetFilters}
            >
              {t("Reset filters")}
            </button>
          </div>
        ) : (
          visible.map((tool, index) => {
            const category = mcpActionGroup(tool.name);
            const showHeading =
              largeCatalog &&
              category &&
              category !== mcpActionGroup(visible[index - 1]?.name ?? "");
            return (
              <div key={tool.name}>
                {showHeading && (
                  <h3 className="px-3 pt-3 pb-1 text-xs font-semibold tracking-wide text-stone-600 uppercase dark:text-stone-300">
                    {category}
                  </h3>
                )}
                <label className="flex min-h-11 cursor-pointer items-start gap-3 rounded-lg p-3 focus-within:bg-stone-50 hover:bg-stone-50 dark:focus-within:bg-white/5 dark:hover:bg-white/5">
                  <input
                    type="checkbox"
                    className="mt-1 size-4 shrink-0 accent-lumen-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lumen-600"
                    checked={selected.has(tool.name)}
                    onChange={(event) =>
                      onSelectionChange([tool.name], event.target.checked)
                    }
                    aria-labelledby={`${id}-name-${index}`}
                    aria-describedby={
                      tool.description
                        ? `${id}-description-${index}`
                        : undefined
                    }
                  />
                  <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">
                    <strong
                      id={`${id}-name-${index}`}
                      className="block text-sm font-medium"
                    >
                      {tool.title || tool.name}
                    </strong>
                    {tool.title && tool.title !== tool.name && (
                      <code className="mt-0.5 block text-xs text-stone-500 dark:text-stone-400">
                        {tool.name}
                      </code>
                    )}
                    {tool.description && (
                      <span
                        id={`${id}-description-${index}`}
                        className="mt-1 block text-xs leading-5 text-stone-600 dark:text-stone-300"
                      >
                        {tool.description}
                      </span>
                    )}
                  </span>
                </label>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
